import { useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useEffect } from 'react';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { ParticipantStatus } from '../../types';
import { ClientInformationDrawerTab, ProviderDrawerState, drawerStateAtom, selectedClientInfoAtom } from '../state';
import { useGetMeasuresByPatientShortId } from '../useGetMeasuresByPatientShortId';
import { ClientInformationDrawerBodyWrapper as ClientInformationDrawerBody } from './ClientInformationDrawerBody';
import { ClientInformationDrawerFooterWrapper as ClientInformationDrawerFooter } from './ClientInformationDrawerFooter';
import { ClientInformationDrawerHeaderWrapper as ClientInformationDrawerHeader } from './ClientInformationDrawerHeader';
import { ClientInformationSchedulingDrawerFooterWrapper as ClientInformationSchedulingDrawerFooter } from './ClientInformationSchedulingDrawerFooter';
import { ClientMoodWrapper as ClientMood } from './ClientMood';
import { activeClientInformationTabAtom } from './state';

type ClientInformationDrawerProps = {
  visitorName?: string;
  patientShortId?: string;
  headerCaption?: ReactNode;
  isEmpty: boolean;
  visitorStatus?: ParticipantStatus;
  visitorUuid?: string;
  onClickBackButton: () => void;
  selectedPatientShortId: string | undefined;
  isInfoTabActive?: boolean;
};

export function ClientInformationDrawer({
  headerCaption,
  isEmpty,
  visitorName,
  visitorStatus,
  visitorUuid,
  onClickBackButton,
  selectedPatientShortId,
  isInfoTabActive,
}: ClientInformationDrawerProps) {
  const isScheduledClient = visitorStatus === ParticipantStatus.SCHEDULED;
  return (
    <>
      <ClientInformationDrawerHeader
        visitorName={visitorName}
        drawerTitleCaption={headerCaption}
        visitorStatus={visitorStatus}
        selectedPatientShortId={selectedPatientShortId || ''}
        onClickBackButton={isScheduledClient ? undefined : onClickBackButton}
      />
      <ClientInformationDrawerBody isEmpty={isEmpty} />
      {isScheduledClient && isInfoTabActive && <ClientInformationSchedulingDrawerFooter />}
      {!isScheduledClient && (
        <ClientInformationDrawerFooter
          visitorStatus={visitorStatus}
          visitorUuid={visitorUuid || ''}
          visitorName={visitorName || ''}
          patientShortId={selectedPatientShortId || ''}
        />
      )}
    </>
  );
}

export function ClientInformationDrawerWrapper() {
  const selectedClient = useAtomValue(selectedClientInfoAtom);
  // Ensures patient measures are fetched when a provider opens the drawer via the upcoming appointment list
  useGetMeasuresByPatientShortId(selectedClient?.patientShortId);
  const activeClientInformationTab = useAtomValue(activeClientInformationTabAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);

  const { userViewed } = useTrackEvent();
  const onClickBackButton = () => {
    setDrawerState(ProviderDrawerState.CLIENTS);
  };

  useEffect(() => {
    userViewed(EventTypeEnum.CLIENT_INFO, {
      patientShortId: selectedClient?.patientShortId ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ClientInformationDrawer
      headerCaption={<ClientMood visitorUuid={selectedClient?.visitorUuid} />}
      selectedPatientShortId={selectedClient?.patientShortId}
      visitorName={selectedClient?.name ?? 'Client Information'}
      visitorStatus={selectedClient?.status}
      visitorUuid={selectedClient?.visitorUuid}
      onClickBackButton={onClickBackButton}
      isEmpty={!selectedClient}
      isInfoTabActive={activeClientInformationTab === ClientInformationDrawerTab.INFORMATION}
    />
  );
}
